import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import KNLoader from 'components/KNLoader/KNLoader'
import { Role } from 'context/authentication/Role.types'
import { useCompaniesView } from 'context/CompaniesViewContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { hasAccess } from 'global/helpers/authorization'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import BulkCreateCompaniesDialog from './BulkCreateCompaniesDialog'
import CompanyFilters from './CompanyFilters'
import { COMPANIES_QUERY_KEY, getFilteredCompanies } from './CompanyManager.service'
import CompanyTable from './CompanyTable'
import CreateOrEditCompanyDialog from './CreateOrEditCompanyDialog'

const CompanyManager = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, page } = useCompaniesView()
  const companiesQuery = useQuery({
    queryKey: [COMPANIES_QUERY_KEY, { page, filters }],
    queryFn: () => getFilteredCompanies(page, filters),
    placeholderData: keepPreviousData,
  })
  const createOrEditCompanyDialog = useDialog<typeof CreateOrEditCompanyDialog>()
  const bulkCreateCompaniesDialog = useDialog<typeof BulkCreateCompaniesDialog>()

  return (
    <Container maxWidth="xxl">
      {companiesQuery.isPending && (
        <KNLoader>
          <Typography>{t('screens.shared.company_manager.loading')}</Typography>
        </KNLoader>
      )}
      {companiesQuery.isError && (
        <Alert severity="error">{getRequestErrorMessage(companiesQuery.error)}</Alert>
      )}
      {companiesQuery.isSuccess && (
        <>
          <Paper sx={{ padding: 2 }}>
            <Stack direction="column" spacing={1} mb={1}>
              <CompanyFilters />
              <Stack
                spacing={1}
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'start', md: 'center' }}
                mb={2}
              >
                <Typography variant="h6">
                  {t('screens.shared.company_manager.companies_count', { count: companiesQuery.data?.totalElements })}
                </Typography>
                {hasAccess(Role.Admin) && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      onClick={() => {
                        createOrEditCompanyDialog.handleOpen()
                        analyticsEvent('polestar_cv_add_company_button_clicked')
                      }}
                      variant="contained"
                      color="secondary"
                      size="small"
                    >
                      {t('screens.shared.company_manager.card.actions.new')}
                    </Button>
                    <Button
                      onClick={bulkCreateCompaniesDialog.handleOpen}
                      variant="contained"
                      color="secondary"
                      size="small"
                    >
                      {t('screens.shared.company_manager.card.actions.bulk')}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Stack>
            <CompanyTable
              companies={companiesQuery.data?.content ?? []}
              pageCount={companiesQuery.data?.totalPages}
            />
          </Paper>
          {hasAccess(Role.Admin) && (
            <>
              <CreateOrEditCompanyDialog
                open={createOrEditCompanyDialog.isOpen}
                onAction={createOrEditCompanyDialog.handleAction}
                onClose={createOrEditCompanyDialog.handleClose}
              />
              <BulkCreateCompaniesDialog
                open={bulkCreateCompaniesDialog.isOpen}
                onAction={bulkCreateCompaniesDialog.handleAction}
                onClose={bulkCreateCompaniesDialog.handleClose}
              />
            </>
          )}
        </>
      )}
    </Container>
  )
}

export default CompanyManager
